import { falAngleLeft } from 'shared/@generated/icons/falAngleLeft';
import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { falTimes } from 'shared/@generated/icons/falTimes';
import { fasPlay } from 'shared/@generated/icons/fasPlay';
import { GestureResponderEvent, Pressable, View } from 'react-native';
import { Image } from 'shared/components/image';
import { Loader } from 'shared/components/loader';
import { Video } from 'shared/components/video';
import { MediaAsset, MediaAssetType } from 'shared/@generated/models';
import Modal from 'shared/components/lazy-modal.web';
import { Icon } from 'shared/components/icon';
import {
  forwardRef,
  type ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { lockScroll, unlockScroll } from 'shared/library/scroll-lock';
import {
  getMediaAssetVideoPosterUrl,
  getMediaAssetVideoUrl,
  getMediaAssetVideoFallbackUrl,
} from 'shared/library/media-asset-urls';
import { getGallerySlideKey, getGallerySlides } from './gallery-slides';

export interface GalleryFullscreenModalRef {
  open: (index?: number) => void;
  openSlide: (index: number) => void;
  close: () => void;
}

interface GalleryFullscreenModalProps {
  items: MediaAsset[];
  defaultAlt: string;
  initialIndex?: number;
  aspect?: 'square' | 'video';
  adSlide?: ReactNode;
  onSelectedIndexChange?: (index: number) => void;
}

const SWIPE_THRESHOLD = 50;

export const GalleryFullscreenModal = forwardRef<
  GalleryFullscreenModalRef,
  GalleryFullscreenModalProps
>(
  (
    { items, defaultAlt, initialIndex = 0, aspect = 'video', adSlide, onSelectedIndexChange },
    ref,
  ) => {
    const { t } = useTranslation('components');
    const slides = useMemo(() => getGallerySlides(items, !!adSlide), [adSlide, items]);
    const initialSlideIndex = useMemo(() => {
      const slideIndex = slides.findIndex(
        (slide) => slide.type === 'media' && slide.mediaIndex === initialIndex,
      );

      return slideIndex === -1 ? 0 : slideIndex;
    }, [initialIndex, slides]);
    const [isMounted, setIsMounted] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(initialSlideIndex);
    const [loadedMediaIds, setLoadedMediaIds] = useState<Set<string>>(() => new Set());
    const scrollLockedRef = useRef(false);
    const modalHasOpened = useRef(false);
    const touchStartXRef = useRef<number | null>(null);
    const thumbnailStripRef = useRef<View>(null);
    const imageAspectRatio = aspect === 'square' ? 1 : 4 / 3;
    const selectedSlide = slides[selectedIndex];
    const selectedItem = selectedSlide?.type === 'media' ? selectedSlide.item : undefined;
    const hasMultipleItems = slides.length > 1;
    const isSelectedImageLoading =
      selectedItem?.type === MediaAssetType.Image && !loadedMediaIds.has(selectedItem.uuid);

    const markMediaAsLoaded = useCallback((uuid: string) => {
      setLoadedMediaIds((loadedIds) => {
        if (loadedIds.has(uuid)) return loadedIds;

        const nextLoadedIds = new Set(loadedIds);
        nextLoadedIds.add(uuid);
        return nextLoadedIds;
      });
    }, []);

    const close = useCallback(() => {
      setIsVisible(false);
      // A pending lazy import has no exit animation or onModalHide callback.
      if (!modalHasOpened.current) setIsMounted(false);

      if (scrollLockedRef.current) {
        unlockScroll();
        scrollLockedRef.current = false;
      }
    }, []);

    const open = useCallback(
      (mediaIndex = initialIndex) => {
        const slideIndex = slides.findIndex(
          (slide) => slide.type === 'media' && slide.mediaIndex === mediaIndex,
        );

        setSelectedIndex(slideIndex === -1 ? 0 : slideIndex);
        setIsMounted(true);
        setIsVisible(true);

        if (!scrollLockedRef.current) {
          lockScroll();
          scrollLockedRef.current = true;
        }
      },
      [initialIndex, slides],
    );

    const openSlide = useCallback(
      (slideIndex: number) => {
        setSelectedIndex(Math.max(0, Math.min(slideIndex, slides.length - 1)));
        setIsMounted(true);
        setIsVisible(true);

        if (!scrollLockedRef.current) {
          lockScroll();
          scrollLockedRef.current = true;
        }
      },
      [slides.length],
    );

    const selectPrevious = useCallback(() => {
      setSelectedIndex((index) => (index - 1 + slides.length) % slides.length);
    }, [slides.length]);

    const selectNext = useCallback(() => {
      setSelectedIndex((index) => (index + 1) % slides.length);
    }, [slides.length]);

    useEffect(() => {
      if (!isVisible) return;

      const handleKeyDown = (event: KeyboardEvent) => {
        if ((event.target as HTMLElement | null)?.tagName === 'VIDEO') return;

        if (event.key === 'Escape') {
          close();
        } else if (hasMultipleItems && event.key === 'ArrowLeft') {
          event.preventDefault();
          selectPrevious();
        } else if (hasMultipleItems && event.key === 'ArrowRight') {
          event.preventDefault();
          selectNext();
        }
      };

      window.addEventListener('keydown', handleKeyDown);

      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [close, hasMultipleItems, isVisible, selectNext, selectPrevious]);

    const scrollSelectedThumbnailIntoView = useCallback(() => {
      const thumbnailStrip = thumbnailStripRef.current as unknown as HTMLDivElement | null;
      const selectedThumbnail = thumbnailStrip?.querySelector(
        `[data-gallery-index="${selectedIndex}"]`,
      );

      selectedThumbnail?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }, [selectedIndex]);

    useEffect(() => {
      if (!isVisible) return;

      onSelectedIndexChange?.(selectedIndex);
      scrollSelectedThumbnailIntoView();
    }, [isVisible, onSelectedIndexChange, scrollSelectedThumbnailIntoView, selectedIndex]);

    useEffect(() => {
      return () => {
        if (scrollLockedRef.current) unlockScroll();
      };
    }, []);

    useImperativeHandle(ref, () => ({ open, openSlide, close }), [close, open, openSlide]);

    const handleTouchStart = (event: GestureResponderEvent) => {
      const eventTarget = event.target as unknown as HTMLElement;
      if (eventTarget.closest?.('video')) return;

      touchStartXRef.current =
        event.nativeEvent.changedTouches[0]?.pageX ?? event.nativeEvent.pageX ?? null;
    };

    const handleTouchEnd = (event: GestureResponderEvent) => {
      if (touchStartXRef.current === null) return;

      const touchEndX = event.nativeEvent.changedTouches[0]?.pageX ?? event.nativeEvent.pageX;
      const distance = touchEndX === undefined ? 0 : touchEndX - touchStartXRef.current;

      touchStartXRef.current = null;

      if (!hasMultipleItems || Math.abs(distance) < SWIPE_THRESHOLD) return;

      if (distance > 0) {
        selectPrevious();
      } else {
        selectNext();
      }
    };

    if (!isMounted) return null;

    return (
      <Modal
        isVisible={isVisible}
        onLoadingCancel={close}
        animationIn="fadeIn"
        animationOut="fadeOut"
        hideModalContentWhileAnimating={true}
        onBackButtonPress={close}
        onBackdropPress={close}
        onModalWillShow={() => {
          modalHasOpened.current = true;
        }}
        onModalShow={scrollSelectedThumbnailIntoView}
        onModalHide={() => {
          modalHasOpened.current = false;
          setIsMounted(false);
        }}
        backdropOpacity={1}
        customBackdrop={
          <Pressable
            onPress={close}
            className="absolute inset-0 cursor-default bg-black outline-none"
            testID="modal-backdrop"
          />
        }
        style={{ margin: 0 }}
      >
        <View className="h-dvh w-full bg-black" testID="gallery-fullscreen">
          <View className="h-14 shrink-0 flex-row items-center justify-between px-3 md:h-16 md:px-5">
            <View className="size-11" />

            <span
              className="font-regular text-base font-normal text-white md:text-xl"
              data-testid="gallery-fullscreen-counter"
            >
              {selectedIndex + 1} <span>/ {slides.length}</span>
            </span>

            <button
              type="button"
              aria-label="Close gallery"
              data-testid="gallery-fullscreen-close"
              onClick={close}
              className="focus-visible:ring-brand-green flex size-10 cursor-pointer items-center justify-center rounded border border-white/20 bg-black/20 text-white transition-colors hover:bg-black/35 focus:outline-none focus-visible:ring-2 md:size-12"
            >
              <Icon icon={falTimes} className="size-5 text-white md:size-6" />
            </button>
          </View>

          <View
            className="relative flex min-h-0 flex-1 touch-pan-y items-center justify-center md:px-20"
            testID="gallery-fullscreen-media"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {selectedSlide && (
              <View className="relative flex size-full max-w-screen-2xl items-center justify-center overflow-hidden">
                {selectedSlide.type === 'ad' ? (
                  <View
                    className="flex size-full items-center justify-center px-3"
                    testID="gallery-fullscreen-ad"
                  >
                    {adSlide}
                  </View>
                ) : selectedSlide.item.type === MediaAssetType.Video ? (
                  <Video
                    key={selectedSlide.item.uuid}
                    src={getMediaAssetVideoUrl(selectedSlide.item)}
                    fallbackSrc={getMediaAssetVideoFallbackUrl(selectedSlide.item)}
                    poster={getMediaAssetVideoPosterUrl(selectedSlide.item)}
                    containerClassName="size-full"
                    videoClassName="object-contain"
                    autoPlay={false}
                    controls={true}
                    loop={true}
                    muted={false}
                    sizeType="full"
                  />
                ) : (
                  <Image
                    key={selectedSlide.item.uuid}
                    src={selectedSlide.item}
                    alt={selectedSlide.item.description ?? defaultAlt}
                    containerClassName="relative size-full"
                    imageClassName="object-contain"
                    width={1280}
                    sizes="100vw"
                    loading="eager"
                    onLoad={() => markMediaAsLoaded(selectedSlide.item.uuid)}
                    onError={() => markMediaAsLoaded(selectedSlide.item.uuid)}
                  />
                )}
              </View>
            )}

            {isSelectedImageLoading && (
              <View
                accessibilityRole="progressbar"
                accessibilityLiveRegion="polite"
                className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center"
                testID="gallery-fullscreen-loading"
              >
                <Loader dense={true} light={true} />
              </View>
            )}

            {hasMultipleItems && (
              <>
                <button
                  type="button"
                  aria-label="Previous media"
                  data-testid="gallery-fullscreen-previous"
                  onClick={selectPrevious}
                  className="focus-visible:ring-brand-green absolute left-2 top-1/2 flex size-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded border border-white/20 bg-black/20 text-white transition-colors hover:bg-black/35 focus:outline-none focus-visible:ring-2 md:left-5 md:size-12"
                >
                  <Icon icon={falAngleLeft} className="size-5 text-white md:size-6" />
                </button>

                <button
                  type="button"
                  aria-label="Next media"
                  data-testid="gallery-fullscreen-next"
                  onClick={selectNext}
                  className="focus-visible:ring-brand-green absolute right-2 top-1/2 flex size-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded border border-white/20 bg-black/20 text-white transition-colors hover:bg-black/35 focus:outline-none focus-visible:ring-2 md:right-5 md:size-12"
                >
                  <Icon icon={falAngleRight} className="size-5 text-white md:size-6" />
                </button>
              </>
            )}
          </View>

          {selectedItem?.description && (
            <View className="shrink-0 items-center px-3 py-2">
              <span className="font-regular max-w-screen-md truncate text-center text-sm text-white">
                {selectedItem.description}
              </span>
            </View>
          )}

          {hasMultipleItems && (
            <View className="h-24 shrink-0 justify-center border-t border-white/10 px-3 md:h-28 md:px-5">
              <View
                ref={thumbnailStripRef}
                className="max-w-full flex-row gap-2 overflow-x-auto overflow-y-hidden py-2"
                testID="gallery-fullscreen-thumbnails"
              >
                {slides.map((slide, index) => {
                  const isSelected = selectedIndex === index;

                  if (slide.type === 'ad') {
                    return (
                      <button
                        key={slide.key}
                        type="button"
                        aria-label={t('gallery.ad')}
                        aria-current={isSelected ? 'true' : undefined}
                        data-gallery-index={index}
                        data-testid={`gallery-fullscreen-thumbnail-${index}`}
                        onClick={() => setSelectedIndex(index)}
                        className={`focus-visible:ring-brand-green relative flex h-16 w-10 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded border-2 bg-white/10 transition-opacity focus:outline-none focus-visible:ring-2 md:h-20 md:w-12 ${
                          isSelected
                            ? 'border-white opacity-100'
                            : 'border-transparent opacity-55 hover:opacity-100'
                        }`}
                      >
                        <span className="text-2xs rotate-90 whitespace-nowrap font-semibold uppercase text-white/60">
                          {t('gallery.ad')}
                        </span>
                      </button>
                    );
                  }

                  const item = slide.item;
                  const thumbnailAlt = item.description ?? defaultAlt;

                  return (
                    <button
                      key={getGallerySlideKey(slide)}
                      type="button"
                      aria-label={thumbnailAlt}
                      aria-current={isSelected ? 'true' : undefined}
                      data-gallery-index={index}
                      data-testid={`gallery-fullscreen-thumbnail-${index}`}
                      onClick={() => setSelectedIndex(index)}
                      className={`focus-visible:ring-brand-green relative h-16 w-20 shrink-0 cursor-pointer overflow-hidden rounded border-2 bg-black/30 transition-opacity focus:outline-none focus-visible:ring-2 md:h-20 md:w-28 ${
                        isSelected
                          ? 'border-white opacity-100'
                          : 'border-transparent opacity-55 hover:opacity-100'
                      }`}
                    >
                      {item.type === MediaAssetType.Video ? (
                        <>
                          <Image
                            src={getMediaAssetVideoPosterUrl(item)}
                            alt={thumbnailAlt}
                            containerClassName="relative size-full"
                            imageClassName="object-cover"
                            sizeType="card"
                            aspectRatio={imageAspectRatio}
                          />
                          <span className="absolute inset-0 flex items-center justify-center bg-black/20">
                            <Icon icon={fasPlay} className="size-5 text-white" />
                          </span>
                        </>
                      ) : (
                        <Image
                          src={item}
                          alt={thumbnailAlt}
                          containerClassName="relative size-full"
                          imageClassName="object-cover"
                          sizeType="card"
                          aspectRatio={imageAspectRatio}
                          sizes="(max-width: 767px) 80px, 112px"
                        />
                      )}
                    </button>
                  );
                })}
              </View>
            </View>
          )}
        </View>
      </Modal>
    );
  },
);

GalleryFullscreenModal.displayName = 'GalleryFullscreenModal';
